import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type GameSubmissionWithGame, type PitchCount } from '../../services/api';

@customElement('game-approval-queue')
export class GameApprovalQueue extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private pendingGames: GameSubmissionWithGame[] = [];

  @state()
  private loading = true;

  @state()
  private selectedGame: GameSubmissionWithGame | null = null;

  @state()
  private rejectReason = '';

  @state()
  private processing = false;

  @state()
  private error = '';

  @state()
  private toast: { type: 'success' | 'error'; message: string } | null = null;

  @state()
  private pitchCountsByGame: Map<string, PitchCount[]> = new Map();

  /**
   * Search filter for the pending list. Matches against game name,
   * location, submitted-by name, and the formatted date. Local-only —
   * we already have the full list in memory.
   */
  @state()
  private searchQuery = '';

  async connectedCallback() {
    super.connectedCallback();
    await this.loadPendingGames();
  }

  private async loadPendingGames() {
    this.loading = true;
    this.error = '';
    try {
      this.pendingGames = (await api.getPendingGames()) || [];

      // Fetch pitch counts for each pending game in parallel
      const pitchCountResults = await Promise.all(
        this.pendingGames.map(async (game) => {
          try {
            const pitches = (await api.getGamePitches(game.game_id)) || [];
            return { gameId: game.game_id, pitches };
          } catch {
            return { gameId: game.game_id, pitches: [] };
          }
        })
      );

      // Store pitch counts in the map
      this.pitchCountsByGame = new Map(
        pitchCountResults.map((r) => [r.gameId, r.pitches])
      );
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load';
    } finally {
      this.loading = false;
    }
  }

  private async approve(submissionId: string) {
    this.processing = true;
    try {
      await api.approveGame(submissionId);
      this.pendingGames = this.pendingGames.filter((g) => g.id !== submissionId);
      this.showToast('success', 'Game approved successfully');
    } catch (err) {
      this.showToast(
        'error',
        err instanceof Error ? err.message : 'Approval failed'
      );
    } finally {
      this.processing = false;
    }
  }

  private async reject() {
    if (!this.selectedGame) return;
    if (!this.rejectReason.trim()) {
      this.showToast('error', 'Please provide a rejection reason');
      return;
    }

    this.processing = true;
    try {
      await api.rejectGame(this.selectedGame.id, this.rejectReason);
      this.pendingGames = this.pendingGames.filter(
        (g) => g.id !== this.selectedGame?.id
      );
      this.selectedGame = null;
      this.rejectReason = '';
      this.showToast('success', 'Game rejected');
    } catch (err) {
      this.showToast(
        'error',
        err instanceof Error ? err.message : 'Rejection failed'
      );
    } finally {
      this.processing = false;
    }
  }

  private showToast(type: 'success' | 'error', message: string) {
    this.toast = { type, message };
    setTimeout(() => {
      this.toast = null;
    }, 3000);
  }

  private closeModal() {
    this.selectedGame = null;
    this.rejectReason = '';
  }

  render() {
    return html`
      <div class="space-y-4">
        <div class="flex justify-between items-center">
          <h2 class="text-lg sm:text-xl font-semibold">Game Approval Queue</h2>
          <button class="btn btn-sm btn-ghost gap-1" @click=${this.loadPendingGames}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span class="hidden sm:inline">Refresh</span>
          </button>
        </div>

        ${this.loading
          ? html`
              <div class="space-y-4">
                ${[1, 2, 3].map(
                  () => html`<div class="skeleton h-24 w-full"></div>`
                )}
              </div>
            `
          : this.error
            ? html`
                <div class="alert alert-error">
                  <span>${this.error}</span>
                  <button class="btn btn-sm" @click=${this.loadPendingGames}>
                    Retry
                  </button>
                </div>
              `
            : this.pendingGames.length === 0
              ? html`
                  <div
                    class="text-center py-12 bg-base-200 rounded-lg text-base-content/70"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="mx-auto h-12 w-12 mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p class="text-lg">No games pending approval</p>
                    <p class="text-sm mt-2">
                      All submitted games have been reviewed.
                    </p>
                  </div>
                `
              : this.renderPendingList()}
        ${this.selectedGame ? this.renderRejectModal() : nothing}
        ${this.renderToast()}
      </div>
    `;
  }

  private getFilteredPending(): GameSubmissionWithGame[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.pendingGames;
    return this.pendingGames.filter((g) => {
      const dateStr = new Date(g.game_date).toLocaleDateString().toLowerCase();
      const isoDate = g.game_date.slice(0, 10).toLowerCase();
      return (
        g.game_name.toLowerCase().includes(q) ||
        (g.location ?? '').toLowerCase().includes(q) ||
        (g.submitted_by_name ?? '').toLowerCase().includes(q) ||
        dateStr.includes(q) ||
        isoDate.includes(q)
      );
    });
  }

  private renderPendingList() {
    const filtered = this.getFilteredPending();
    return html`
      <div class="space-y-4">
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <div class="badge badge-warning badge-lg">
            ${this.pendingGames.length} pending
          </div>
        </div>

        <!-- Search filter -->
        <div class="relative">
          <input
            type="text"
            class="input input-bordered input-sm w-full pl-9"
            data-testid="approval-queue-search-input"
            placeholder="Search by game, team, date, or submitter..."
            .value=${this.searchQuery}
            @input=${(e: Event) => { this.searchQuery = (e.target as HTMLInputElement).value; }}
          />
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          ${this.searchQuery ? html`
            <button
              class="btn btn-ghost btn-xs btn-circle absolute right-2 top-1/2 -translate-y-1/2"
              aria-label="Clear search"
              @click=${() => { this.searchQuery = ''; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ` : nothing}
        </div>

        ${this.searchQuery
          ? html`
              <div class="text-xs text-base-content/60">
                ${filtered.length} of ${this.pendingGames.length} match
              </div>
            `
          : nothing}

        ${filtered.length === 0 && this.searchQuery
          ? html`
              <div class="text-center py-8 text-base-content/60 border border-dashed border-base-300 rounded-lg">
                <p>No pending games match "${this.searchQuery}"</p>
              </div>
            `
          : filtered.map((game) => this.renderPendingGame(game))}
      </div>
    `;
  }

  private renderPendingGame(game: GameSubmissionWithGame) {
    const pitchCounts = this.pitchCountsByGame.get(game.game_id) || [];
    const totalPitches = pitchCounts.reduce((sum, pc) => sum + pc.count, 0);

    return html`
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body p-4 sm:p-6">
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div class="flex-1 min-w-0">
              <h4 class="font-medium text-lg">${game.game_name}</h4>
              <div class="text-sm text-base-content/70 space-y-1">
                <p>
                  <span class="font-medium">Date:</span>
                  ${new Date(game.game_date).toLocaleDateString()}
                  ${game.location
                    ? html` - <span>${game.location}</span>`
                    : nothing}
                </p>
                <p>
                  <span class="font-medium">Submitted by:</span>
                  ${game.submitted_by_name || 'Unknown'}
                  <span class="text-xs">
                    (${new Date(game.submitted_at).toLocaleString()})
                  </span>
                </p>
              </div>

              <!-- Pitch Counts Section -->
              <div class="mt-3 pt-3 border-t border-base-200">
                <div class="flex items-center gap-2 mb-2">
                  <span class="font-medium text-sm">Pitch Counts</span>
                  <span class="badge badge-sm badge-primary">${totalPitches} total</span>
                </div>
                ${pitchCounts.length > 0
                  ? html`
                      <div class="flex flex-wrap gap-2">
                        ${pitchCounts.map(
                          (pc) => html`
                            <div class="badge badge-outline gap-1">
                              ${pc.jersey_number != null
                                ? html`<span class="text-xs text-base-content/50">#${pc.jersey_number}</span>`
                                : nothing}
                              <span>${pc.player_name}</span>
                              <span class="font-semibold">${pc.count}</span>
                            </div>
                          `
                        )}
                      </div>
                    `
                  : html`<p class="text-sm text-base-content/50">No pitches recorded</p>`}
              </div>
            </div>
            <div class="flex gap-2 sm:flex-col sm:gap-2 sm:ml-2">
              <button
                class="btn btn-success flex-1 sm:flex-none sm:btn-sm"
                ?disabled=${this.processing}
                @click=${() => this.approve(game.id)}
              >
                ${this.processing
                  ? html`<span class="loading loading-spinner loading-xs"></span>`
                  : html`
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    `}
                Approve
              </button>
              <button
                class="btn btn-error btn-outline flex-1 sm:flex-none sm:btn-sm"
                ?disabled=${this.processing}
                @click=${() => {
                  this.selectedGame = game;
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderRejectModal() {
    return html`
      <div class="modal modal-open">
        <div class="modal-box w-11/12 max-w-lg">
          <h3 class="font-bold text-lg">Reject Game Submission</h3>
          <p class="py-2 text-base-content/70">
            Rejecting: <span class="font-medium">${this.selectedGame?.game_name}</span>
          </p>

          <div class="form-control">
            <label class="label">
              <span class="label-text">Reason for rejection *</span>
            </label>
            <textarea
              class="textarea textarea-bordered h-24"
              placeholder="Please provide a reason for rejecting this submission..."
              .value=${this.rejectReason}
              @input=${(e: Event) => {
                this.rejectReason = (e.target as HTMLTextAreaElement).value;
              }}
            ></textarea>
          </div>

          <div class="modal-action flex-col-reverse sm:flex-row gap-2">
            <button
              class="btn btn-ghost w-full sm:w-auto"
              @click=${this.closeModal}
              ?disabled=${this.processing}
            >
              Cancel
            </button>
            <button
              class="btn btn-error w-full sm:w-auto"
              @click=${this.reject}
              ?disabled=${this.processing || !this.rejectReason.trim()}
            >
              ${this.processing
                ? html`<span class="loading loading-spinner loading-sm"></span>`
                : nothing}
              Reject Submission
            </button>
          </div>
        </div>
        <div class="modal-backdrop" @click=${this.closeModal}></div>
      </div>
    `;
  }

  private renderToast() {
    if (!this.toast) return nothing;

    return html`
      <div class="toast toast-end">
        <div
          class="alert ${this.toast.type === 'success'
            ? 'alert-success'
            : 'alert-error'}"
        >
          <span>${this.toast.message}</span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-approval-queue': GameApprovalQueue;
  }
}
